<template>
  <BlockFieldset class="project-parties">
    <p v-if="isNewProject" class="text-muted">{{ $t('saveNewProjectFirst') }}</p>
    <div v-else>
      <b-row>
        <b-col v-for="partyList in partyLists" :key="partyList.field" lg="6" md="6" sm="12">
          <div class="party-section pt-2 pb-4">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <strong>{{ $t(partyList.field) }}</strong>
              <button
                class="btn btn-sm btn-success"
                @click="openPartyBrowser(partyList)"
                type="button"
                style="white-space: nowrap"
              >
                <i class="fa fa-plus mr-1"></i>
                {{ $t(partyList.addLabelKey) }}
              </button>
            </div>
            <ProjectPartyList
              :parties="project[partyList.field] || []"
              @update-parties="(parties) => updateParties(partyList.field, parties)"
            />
          </div>
        </b-col>
      </b-row>

      <div v-if="!isBlockReadOnly" class="text-right mt-2">
        <b-button variant="bg" :disabled="isSaving" @click="save">
          {{ $t('saveNamed', { name: $t('project_parties') }) }}
        </b-button>
      </div>

      <Dialog
        :header="browsedPartyList ? $t(browsedPartyList.addLabelKey) : ''"
        :visible.sync="isPartyBrowserVisible"
        :modal="true"
        :style="{ width: '70vw', maxWidth: '1200px' }"
        :contentStyle="{ height: '60vh', overflow: 'visible' }"
        :closable="true"
      >
        <PersonBrowser
          v-if="browsedPartyList"
          :key="browsedPartyList.field"
          :projectNumber="project.number"
          :eligibleFor="browsedPartyList.eligibleFor"
          :excludeIds="partyIdsOf(browsedPartyList.field)"
          @select="(party) => addParty(browsedPartyList.field, party)"
        />
        <template #footer>
          <button class="btn btn-default" @click="closePartyBrowser">
            {{ $t('close') }}
          </button>
        </template>
      </Dialog>
    </div>
  </BlockFieldset>
</template>

<script>
import { mapActions } from 'vuex';
import Dialog from 'primevue/dialog';
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import PersonBrowser from '@/components/PersonBrowser';
import ProjectPartyList from '@/components/ProjectPartyList';
import { AccessBlockMixin } from '@/mixins/accessBlock';
import { BlockSaveMixin } from '@/mixins/blockSave';
import { toProjectPartiesInput } from './projectPartiesInput';

/** Number of the unsaved project (ProjectEdit `_new`): the block needs a saved project. */
const NEW_PROJECT_NUMBER = '_new';

/** The four lists in the order of the page: label key = field, add button, party kind searched. */
const PARTY_LISTS = [
  {
    field: 'projectRightsOwners',
    addLabelKey: 'addProjectRightsOwner',
    eligibleFor: 'PROJECT_RIGHTS_OWNER',
  },
  {
    field: 'projectRightsBrokers',
    addLabelKey: 'addProjectRightsBroker',
    eligibleFor: 'PROJECT_RIGHTS_BROKER',
  },
  { field: 'investors', addLabelKey: 'addInvestor', eligibleFor: 'INVESTOR' },
  { field: 'investorBrokers', addLabelKey: 'addInvestorBroker', eligibleFor: 'INVESTOR_BROKER' },
];

/**
 * Block project-parties ("Geschäftspartner", plan docs/work/_done/2026-10-project-block-parties): the
 * four party lists of the project. Add opens the party search of the block, remove takes a party
 * out; both change only the stored project, the own button saves the lists. Add and remove are
 * native buttons, so the fieldset disables them for readers; the link to the party stays usable.
 * On the unsaved project it shows only a hint (decided 3). Emits `dirty` and `saved`.
 */
export default {
  name: 'ProjectParties',
  components: {
    BlockFieldset,
    Dialog,
    PersonBrowser,
    ProjectPartyList,
  },
  mixins: [AccessBlockMixin, BlockSaveMixin],
  props: {
    blockId: { type: String, required: true },
    project: { type: Object, required: true },
  },
  data() {
    return {
      partyLists: PARTY_LISTS,
      /** List whose party search is open; null when closed. */
      browsedPartyList: null,
    };
  },
  computed: {
    isNewProject() {
      return this.project.number === NEW_PROJECT_NUMBER;
    },
    blockInput() {
      return toProjectPartiesInput(this.project);
    },
    isPartyBrowserVisible: {
      get() {
        return this.browsedPartyList !== null;
      },
      set(isVisible) {
        if (!isVisible) {
          this.closePartyBrowser();
        }
      },
    },
  },
  methods: {
    ...mapActions(['updateProjectParties']),
    saveBlockInput(input) {
      return this.updateProjectParties({ projectNumber: this.project.number, input });
    },
    openPartyBrowser(partyList) {
      this.browsedPartyList = partyList;
    },
    closePartyBrowser() {
      this.browsedPartyList = null;
    },
    partyIdsOf(field) {
      return (this.project[field] || []).map((party) => party._id);
    },
    addParty(field, party) {
      if (!this.project[field]) {
        this.$set(this.project, field, []);
      }
      const isAlreadyAssigned = this.project[field].some((existing) => existing._id === party._id);
      if (!isAlreadyAssigned) {
        this.project[field] = [...this.project[field], party];
      }
    },
    updateParties(field, parties) {
      this.project[field] = parties;
    },
  },
};
</script>
